import { useEffect, useState } from 'react';

import styles from './ServerColorIndicator.module.css';

export type TServerColor = 'blue' | 'green';

export function isShowColorEnabled(): boolean {
  const value = import.meta.env.VITE_IS_SHOW_COLOR;
  return typeof value === 'string' && value === 'true';
}

export function resolveIndicatorColor(raw: unknown): TServerColor | null {
  if (raw === 'blue' || raw === 'green') {
    return raw;
  }
  return null;
}

export async function fetchServerColor(
  windowFetch: typeof window.fetch,
): Promise<TServerColor | null> {
  try {
    const response = await windowFetch('/__server-color');
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const data = (await response.json()) as unknown;
    const rawColor =
      typeof data === 'object' && data !== null
        ? (data as Record<string, unknown>).color
        : undefined;
    const resolved = resolveIndicatorColor(rawColor);

    if (resolved) {
      return resolved;
    }

    console.error(
      'ServerColorIndicator: invalid or null color from endpoint',
      data,
    );
    return null;
  } catch (error) {
    console.error('ServerColorIndicator: failed to fetch server color', error);
    return null;
  }
}

export interface IServerColorIndicatorViewProps {
  isEnabled: boolean;
  hasError: boolean;
  color: TServerColor | null;
}

export function ServerColorIndicatorView({
  color,
  hasError,
  isEnabled,
}: IServerColorIndicatorViewProps) {
  if (!isEnabled || hasError || !color) {
    return (
      <div data-testid="server-color-indicator" style={{ display: 'none' }} />
    );
  }

  const backgroundColor = color === 'blue' ? '#0066ff' : '#00cc66';

  return (
    <div
      aria-hidden="true"
      className={styles.indicator}
      data-testid="server-color-indicator"
      style={{ backgroundColor }}
    />
  );
}

export function ServerColorIndicator() {
  const [color, setColor] = useState<TServerColor | null>(null);
  const [hasError, setHasError] = useState(false);

  const isEnabled = isShowColorEnabled();

  useEffect(() => {
    if (!isEnabled || typeof window === 'undefined') {
      return;
    }

    const run = async (): Promise<void> => {
      const result = await fetchServerColor(window.fetch);
      if (result) {
        setColor(result);
      } else {
        setHasError(true);
      }
    };

    void run();
  }, [isEnabled]);

  return (
    <ServerColorIndicatorView
      color={color}
      hasError={hasError}
      isEnabled={isEnabled}
    />
  );
}
